import { useMemo } from 'react';
import { useUserStore } from '../stores/useUserStore';
import { useDevConsoleStore } from '../stores/useDevConsoleStore';

// Define role hierarchy and permissions
export type Role =
  | 'sysAdmin'
  | 'admin'
  | 'orgAdmin'
  | 'storeManager'
  | 'staff'
  | 'cashier';

export type Action = 'list' | 'create' | 'edit' | 'delete' | 'view' | 'manage';

export type Resource =
  | 'dashboard'
  | 'orders'
  | 'orders_report'
  | 'campaigns'
  | 'inventory'
  | 'customers'
  | 'customer_reviews'
  | 'products'
  | 'product_categories'
  | 'recipes'
  | 'ingredients'
  | 'costs'
  | 'cost_categories'
  | 'staff_profiles'
  | 'salary_forecast'
  | 'policies'
  | 'warehouses'
  | 'inventory_categories'
  | 'menuSales'
  | 'menuZalo'
  | 'zalo/checkin'
  | 'menuLibrary'
  | 'books'
  | 'book_categories'
  | 'book_rents'
  | 'menuStaff'
  | 'menuFinancial'
  | 'menuOrganization'
  | 'menus'
  | 'bonuses'
  | 'settings'
  | 'project_management';

// Role hierarchy (higher roles inherit permissions from lower roles)
const ROLE_HIERARCHY: Record<Role, number> = {
  sysAdmin: 5,
  admin: 4,
  orgAdmin: 4,
  storeManager: 3,
  staff: 2,
  cashier: 1,
};

// Resource permissions by role
const ROLE_PERMISSIONS: Record<Role, Record<Resource, Action[]>> = {
  sysAdmin: {
    dashboard: ['list', 'view', 'manage'],
    orders: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    orders_report: ['list', 'view', 'manage'],
    campaigns: [], // sysAdmin should NOT have access to campaigns
    inventory: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    customers: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    customer_reviews: ['list', 'view', 'manage'],
    products: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    product_categories: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    recipes: [], // sysAdmin should NOT have access to recipes
    ingredients: [], // sysAdmin should NOT have access to ingredients
    costs: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    cost_categories: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    staff_profiles: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    salary_forecast: ['list', 'view', 'manage'],
    policies: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    warehouses: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    inventory_categories: [
      'list',
      'create',
      'edit',
      'delete',
      'view',
      'manage',
    ],
    menuSales: ['list', 'view', 'manage'],
    menuZalo: ['list', 'view', 'manage'],
    'zalo/checkin': ['list', 'view', 'manage'],
    menuLibrary: ['list', 'view', 'manage'],
    books: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    book_categories: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    book_rents: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    menuStaff: ['list', 'view', 'manage'],
    menuFinancial: ['list', 'view', 'manage'],
    menuOrganization: ['list', 'view', 'manage'],
    menus: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    bonuses: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    settings: ['list', 'view', 'manage'],
    project_management: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
  },
  admin: {
    dashboard: ['list', 'view', 'manage'],
    orders: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    orders_report: ['list', 'view', 'manage'],
    campaigns: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    inventory: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    customers: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    customer_reviews: ['list', 'view', 'manage'],
    products: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    product_categories: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    recipes: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    ingredients: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    costs: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    cost_categories: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    staff_profiles: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    salary_forecast: ['list', 'view', 'manage'],
    policies: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    warehouses: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    inventory_categories: [
      'list',
      'create',
      'edit',
      'delete',
      'view',
      'manage',
    ],
    menuSales: ['list', 'view', 'manage'],
    menuZalo: ['list', 'view', 'manage'],
    'zalo/checkin': ['list', 'view', 'manage'],
    menuLibrary: ['list', 'view', 'manage'],
    books: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    book_categories: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    book_rents: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    menuStaff: ['list', 'view', 'manage'],
    menuFinancial: ['list', 'view', 'manage'],
    menuOrganization: ['list', 'view', 'manage'],
    menus: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    bonuses: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    settings: ['list', 'view', 'manage'],
    project_management: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
  },
  orgAdmin: {
    dashboard: ['list', 'view', 'manage'],
    orders: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    orders_report: ['list', 'view', 'manage'],
    campaigns: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    inventory: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    customers: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    customer_reviews: ['list', 'view', 'manage'],
    products: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    product_categories: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    recipes: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    ingredients: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    costs: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    cost_categories: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    staff_profiles: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    salary_forecast: ['list', 'view', 'manage'],
    policies: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    warehouses: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    inventory_categories: [
      'list',
      'create',
      'edit',
      'delete',
      'view',
      'manage',
    ],
    menuSales: ['list', 'view', 'manage'],
    menuZalo: ['list', 'view', 'manage'],
    'zalo/checkin': ['list', 'view', 'manage'],
    menuLibrary: ['list', 'view', 'manage'],
    books: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    book_categories: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    book_rents: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    menuStaff: ['list', 'view', 'manage'],
    menuFinancial: ['list', 'view', 'manage'],
    menuOrganization: ['list', 'view', 'manage'],
    menus: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    bonuses: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
    settings: ['list', 'view', 'manage'],
    project_management: ['list', 'create', 'edit', 'delete', 'view', 'manage'],
  },
  storeManager: {
    dashboard: ['list', 'view'],
    orders: ['list', 'create', 'edit', 'view'],
    orders_report: ['list', 'view'],
    campaigns: ['list', 'create', 'edit', 'view'],
    inventory: ['list', 'create', 'edit', 'delete', 'view'],
    recipes: ['list', 'create', 'edit', 'delete', 'view'],
    ingredients: ['list', 'create', 'edit', 'view'],
    customers: ['list', 'create', 'edit', 'view'],
    customer_reviews: ['list', 'view'],
    products: ['list', 'create', 'edit', 'delete', 'view'],
    product_categories: ['list', 'create', 'edit', 'delete', 'view'],
    costs: ['list', 'create', 'edit', 'view'],
    cost_categories: ['list', 'create', 'edit', 'view'],
    staff_profiles: ['list', 'view'],
    salary_forecast: ['list', 'view'],
    policies: ['list', 'create', 'edit', 'view'],
    warehouses: ['list', 'create', 'edit', 'delete', 'view'],
    inventory_categories: ['list', 'create', 'edit', 'delete', 'view'],
    menuSales: ['list', 'view'],
    menuZalo: ['list', 'view'],
    'zalo/checkin': ['list', 'view'],
    menuLibrary: ['list', 'view'],
    books: ['list', 'create', 'edit', 'view'],
    book_categories: ['list', 'create', 'edit', 'view'],
    book_rents: ['list', 'create', 'edit', 'view'],
    menuStaff: ['list', 'view'],
    menuFinancial: ['list', 'view'],
    menuOrganization: ['list', 'view'],
    menus: ['list', 'create', 'edit', 'delete', 'view', 'manage'], // Full access for storeManager
    bonuses: ['list', 'create', 'edit', 'delete', 'view'], // StoreManager can manage bonuses
    settings: ['list', 'view'],
    project_management: ['list', 'create', 'edit', 'delete', 'view'],
  },
  staff: {
    dashboard: ['list', 'view'],
    orders: ['list', 'create', 'edit', 'view'],
    orders_report: [], // No access - API blocks
    campaigns: [], // No access - API blocks
    inventory: ['list', 'view'], // Limited access - can scan QR codes and update stock
    customers: ['list', 'view'], // List only - API blocks create/edit
    customer_reviews: [], // No access for staff
    products: [], // No access - API blocks
    product_categories: [], // No access - API blocks
    recipes: ['list', 'view'], // Read-only access
    ingredients: [], // No access for staff
    costs: ['list', 'create'], // List and create only - API blocks edit/delete
    cost_categories: ['list', 'view'], // List only - API blocks create/edit
    staff_profiles: ['list', 'view'], // Staff can view their own profile
    salary_forecast: [], // No access - staff should not see salary forecast
    warehouses: [], // No access - API blocks
    inventory_categories: ['list', 'view'], // Read-only access
    menuSales: ['list', 'view'],
    menuZalo: ['list', 'view'],
    'zalo/checkin': ['list', 'view'],
    menuLibrary: ['list', 'view'],
    books: ['list', 'view'], // List and view only - API blocks create/edit/delete
    book_categories: ['list', 'view'], // List only - API blocks create/edit
    book_rents: [], // No access - API blocks
    menuStaff: ['list', 'view'],
    menuFinancial: ['list', 'view'],
    menuOrganization: ['list', 'view'],
    menus: [], // No access - staff should not see menus management
    bonuses: [], // No access for staff
    policies: [], // No access for staff
    settings: [], // No access - API blocks
    project_management: [], // No access for staff
  },
  cashier: {
    dashboard: ['list', 'view'],
    orders: ['list', 'create', 'edit', 'view'],
    orders_report: ['list', 'view'],
    campaigns: ['list', 'view'],
    inventory: ['list', 'view'],
    inventory_categories: ['list', 'view'],
    customers: ['list', 'create', 'edit', 'view'],
    customer_reviews: [], // No access for cashier
    products: ['list', 'view'],
    product_categories: ['list', 'view'],
    recipes: [], // No access for cashier
    ingredients: [], // No access for cashier
    costs: ['list', 'view'],
    cost_categories: ['list', 'view'],
    staff_profiles: ['list', 'view'],
    salary_forecast: [], // No access for cashier
    policies: ['list', 'view'], // Cashier can view policies
    warehouses: ['list', 'view'],
    menuSales: ['list', 'view'],
    menuZalo: ['list', 'view'],
    'zalo/checkin': ['list', 'view'],
    menuLibrary: ['list', 'view'],
    books: ['list', 'view'],
    book_categories: ['list', 'view'],
    book_rents: ['list', 'view'],
    menuStaff: ['list', 'view'],
    menuFinancial: ['list', 'view'],
    menuOrganization: ['list', 'view'],
    menus: ['list', 'view'], // Read-only for cashier
    bonuses: [], // No access for cashier
    settings: ['list', 'view'],
    project_management: [], // No access for cashier
  },
};

// Helper function to get user's highest role
const getUserHighestRole = (roles: string[]): Role | null => {
  if (!roles || roles.length === 0) return null;

  let highestRole: Role | null = null;
  let highestLevel = 0;

  for (const role of roles) {
    if (role in ROLE_HIERARCHY) {
      const level = ROLE_HIERARCHY[role as Role];
      if (level > highestLevel) {
        highestLevel = level;
        highestRole = role as Role;
      }
    }
  }

  return highestRole;
};

// Helper function to check if user has any of the specified roles
const hasAnyRole = (userRoles: string[], requiredRoles: Role[]): boolean => {
  if (!userRoles || userRoles.length === 0) return false;
  return requiredRoles.some((role) => userRoles.includes(role));
};

// Helper function to check if user has all of the specified roles
const hasAllRoles = (userRoles: string[], requiredRoles: Role[]): boolean => {
  if (!userRoles || userRoles.length === 0) return false;
  return requiredRoles.every((role) => userRoles.includes(role));
};

// Main RBAC hook
export const useRBAC = () => {
  const { user, isLoading, isAuthenticated } = useUserStore();

  // Get simulated roles from DevConsole store (development mode only)
  // This subscribes to store changes so UI updates when roles change
  const { simulatedRoles, isRoleSimulationEnabled } = useDevConsoleStore();

  // Use simulated roles if enabled, otherwise use original user roles
  const effectiveRoles =
    isRoleSimulationEnabled && simulatedRoles.length > 0
      ? simulatedRoles
      : user?.roles || [];

  const userRoles = effectiveRoles;
  const highestRole = getUserHighestRole(userRoles);

  // Check if user has permission for a specific resource and action
  const hasPermission = (resource: Resource, action: Action): boolean => {
    if (!user || !highestRole) return false;

    const permissions = ROLE_PERMISSIONS[highestRole][resource];
    return permissions?.includes(action) || false;
  };

  // Check if user has any of the specified roles (checks all user roles)
  const hasAnyOfRoles = (roles: Role[]): boolean => {
    return hasAnyRole(userRoles, roles);
  };

  // Check if user has all of the specified roles (checks all user roles)
  const hasAllOfRoles = (roles: Role[]): boolean => {
    return hasAllRoles(userRoles, roles);
  };

  // Check if user has a specific role (checks all user roles)
  const hasRole = (role: Role): boolean => {
    return userRoles.includes(role);
  };

  // Check if user can access a resource (any action)
  const canAccessResource = (resource: Resource): boolean => {
    if (!user || !highestRole) return false;

    const permissions = ROLE_PERMISSIONS[highestRole][resource];
    return permissions && permissions.length > 0;
  };

  // Get all permissions for a resource
  const getResourcePermissions = (resource: Resource): Action[] => {
    if (!user || !highestRole) return [];

    return ROLE_PERMISSIONS[highestRole][resource] || [];
  };

  // Check if user is admin level (sysAdmin, admin, or orgAdmin)
  // Uses highest role to respect role hierarchy
  const isAdminLevel = (): boolean => {
    if (!highestRole) return false;
    return (
      highestRole === 'sysAdmin' ||
      highestRole === 'admin' ||
      highestRole === 'orgAdmin'
    );
  };

  // Check if user is manager level (storeManager or higher)
  // Uses highest role to respect role hierarchy
  const isManagerLevel = (): boolean => {
    if (!highestRole) return false;
    return (
      highestRole === 'sysAdmin' ||
      highestRole === 'admin' ||
      highestRole === 'storeManager'
    );
  };

  // Check if user is staff level (staff or higher)
  // Uses highest role to respect role hierarchy
  const isStaffLevel = (): boolean => {
    if (!highestRole) return false;
    return (
      highestRole === 'sysAdmin' ||
      highestRole === 'admin' ||
      highestRole === 'storeManager' ||
      highestRole === 'staff'
    );
  };

  // Common permission checks
  const canManageInventory = (): boolean => {
    return hasPermission('inventory', 'manage') || isManagerLevel();
  };

  const canManageOrders = (): boolean => {
    return hasPermission('orders', 'manage') || isStaffLevel();
  };

  const canManageCustomers = (): boolean => {
    return hasPermission('customers', 'manage') || isStaffLevel();
  };

  const canManageCosts = (): boolean => {
    return hasPermission('costs', 'manage') || isStaffLevel();
  };

  const canDeleteItems = (): boolean => {
    return isManagerLevel();
  };

  // Memoized values for performance
  const memoizedValues = useMemo(
    () => ({
      userRoles,
      highestRole,
      isAdminLevel: isAdminLevel(),
      isManagerLevel: isManagerLevel(),
      isStaffLevel: isStaffLevel(),
      canManageInventory: canManageInventory(),
      canManageOrders: canManageOrders(),
      canManageCustomers: canManageCustomers(),
      canManageCosts: canManageCosts(),
      canDeleteItems: canDeleteItems(),
    }),
    [userRoles, highestRole]
  );

  return {
    // User info
    user,
    isLoading,
    isAuthenticated,
    userRoles,
    highestRole,

    // Permission checks
    hasPermission,
    hasAnyOfRoles,
    hasAllOfRoles,
    hasRole,
    canAccessResource,
    getResourcePermissions,

    // Role level checks
    isAdminLevel: memoizedValues.isAdminLevel,
    isManagerLevel: memoizedValues.isManagerLevel,
    isStaffLevel: memoizedValues.isStaffLevel,

    // Common permission checks
    canManageInventory: memoizedValues.canManageInventory,
    canManageOrders: memoizedValues.canManageOrders,
    canManageCustomers: memoizedValues.canManageCustomers,
    canManageCosts: memoizedValues.canManageCosts,
    canDeleteItems: memoizedValues.canDeleteItems,
  };
};

// Convenience hooks for specific use cases
export const useInventoryPermissions = () => {
  const { hasPermission, canManageInventory, isManagerLevel } = useRBAC();

  return {
    canView: hasPermission('inventory', 'view'),
    canCreate: hasPermission('inventory', 'create'),
    canEdit: hasPermission('inventory', 'edit'),
    canDelete: hasPermission('inventory', 'delete'),
    canManage: canManageInventory,
    canAccess: hasPermission('inventory', 'list'),
    isManagerLevel,
  };
};

export const useOrderPermissions = () => {
  const { hasPermission, canManageOrders, isStaffLevel } = useRBAC();

  return {
    canView: hasPermission('orders', 'view'),
    canCreate: hasPermission('orders', 'create'),
    canEdit: hasPermission('orders', 'edit'),
    canDelete: hasPermission('orders', 'delete'),
    canManage: canManageOrders,
    canAccess: hasPermission('orders', 'list'),
    isStaffLevel,
  };
};

export const useCostPermissions = () => {
  const { hasPermission, canManageCosts, isStaffLevel } = useRBAC();

  return {
    canView: hasPermission('costs', 'view'),
    canCreate: hasPermission('costs', 'create'),
    canEdit: hasPermission('costs', 'edit'),
    canDelete: hasPermission('costs', 'delete'),
    canManage: canManageCosts,
    canAccess: hasPermission('costs', 'list'),
    isStaffLevel,
  };
};

export const useCustomerPermissions = () => {
  const { hasPermission, canManageCustomers, isStaffLevel } = useRBAC();

  return {
    canView: hasPermission('customers', 'view'),
    canCreate: hasPermission('customers', 'create'),
    canEdit: hasPermission('customers', 'edit'),
    canDelete: hasPermission('customers', 'delete'),
    canManage: canManageCustomers,
    canAccess: hasPermission('customers', 'list'),
    isStaffLevel,
  };
};

// Hook for navigation permissions
export const useNavigationPermissions = () => {
  const { canAccessResource } = useRBAC();

  return {
    canAccessDashboard: canAccessResource('dashboard'),
    canAccessOrders: canAccessResource('orders'),
    canAccessOrdersReport: canAccessResource('orders_report'),
    canAccessInventory: canAccessResource('inventory'),
    canAccessCustomers: canAccessResource('customers'),
    canAccessProducts: canAccessResource('products'),
    canAccessCosts: canAccessResource('costs'),
    canAccessCostCategories: canAccessResource('cost_categories'),
    canAccessStaffProfiles: canAccessResource('staff_profiles'),
    canAccessWarehouses: canAccessResource('warehouses'),
    canAccessMenuSales: canAccessResource('menuSales'),
    canAccessMenuZalo: canAccessResource('menuZalo'),
    canAccessZaloCheckin: canAccessResource('zalo/checkin'),
    canAccessMenuLibrary: canAccessResource('menuLibrary'),
    canAccessBooks: canAccessResource('books'),
    canAccessBookCategories: canAccessResource('book_categories'),
    canAccessBookRents: canAccessResource('book_rents'),
    canAccessMenuStaff: canAccessResource('menuStaff'),
    canAccessMenuFinancial: canAccessResource('menuFinancial'),
    canAccessMenuOrganization: canAccessResource('menuOrganization'),
    canAccessSettings: canAccessResource('settings'),
  };
};
